import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BulkActionResult {
  readonly affected: number;
  readonly changes?: ReadonlyArray<{
    readonly id: string;
    readonly name: string;
    readonly before: Record<string, unknown>;
    readonly after: Record<string, unknown>;
  }>;
}

export interface BulkPendingResult {
  readonly pending_action_id: string;
}

export type BulkResult = BulkActionResult | BulkPendingResult;

export interface BulkPricePayload {
  readonly ids: readonly string[];
  readonly mode: "absolute" | "percent";
  readonly value: number;
  readonly reason?: string;
}

export interface BulkCategoryPayload {
  readonly ids: readonly string[];
  readonly category: string;
}

export interface BulkThresholdPayload {
  readonly ids: readonly string[];
  readonly threshold: number | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Generate an Idempotency-Key header for bulk mutations. Each call must use
 * a fresh key so retries can be safely deduplicated server-side.
 */
function bulkHeaders(): Record<string, string> {
  return {
    "Idempotency-Key": crypto.randomUUID(),
  };
}

// ---------------------------------------------------------------------------
// API module
// ---------------------------------------------------------------------------

export const bulkProductsApi = {
  /** Bulk activate products by ID. */
  activate: (ids: readonly string[]) =>
    adminFetch<BulkResult>("/api/admin/products/bulk/activate", {
      method: "POST",
      body: JSON.stringify({ ids }),
      headers: bulkHeaders(),
    }),

  /** Bulk deactivate products by ID. */
  deactivate: (ids: readonly string[]) =>
    adminFetch<BulkResult>("/api/admin/products/bulk/deactivate", {
      method: "POST",
      body: JSON.stringify({ ids }),
      headers: bulkHeaders(),
    }),

  /** Bulk soft-delete products by ID. May require checker approval. */
  delete: (ids: readonly string[]) =>
    adminFetch<BulkResult>("/api/admin/products/bulk/delete", {
      method: "POST",
      body: JSON.stringify({ ids }),
      headers: bulkHeaders(),
    }),

  /** Bulk restore soft-deleted products. */
  restore: (ids: readonly string[]) =>
    adminFetch<BulkResult>("/api/admin/products/bulk/restore", {
      method: "POST",
      body: JSON.stringify({ ids }),
      headers: bulkHeaders(),
    }),

  /** Bulk price update (absolute amount or percent delta). */
  price: (payload: BulkPricePayload) =>
    adminFetch<BulkResult>("/api/admin/products/bulk/price", {
      method: "POST",
      body: JSON.stringify(payload),
      headers: bulkHeaders(),
    }),

  /** Bulk re-categorize products. */
  category: (payload: BulkCategoryPayload) =>
    adminFetch<BulkResult>("/api/admin/products/bulk/category", {
      method: "POST",
      body: JSON.stringify(payload),
      headers: bulkHeaders(),
    }),

  /** Bulk update low-stock alert threshold. */
  threshold: (payload: BulkThresholdPayload) =>
    adminFetch<BulkResult>("/api/admin/products/bulk/threshold", {
      method: "POST",
      body: JSON.stringify(payload),
      headers: bulkHeaders(),
    }),
} as const;
